import { useEffect, useRef, useState } from 'react'
import { Maximize, Minus, Plus } from 'lucide-react'
import { useTools } from '../../ToolProvider.jsx'

// Cuánto cambia el zoom por píxel que se arrastra el chip: exponencial, para
// que un mismo gesto pese igual a 20 % que a 400 %.
const SCRUB_RATE = 0.008
// Por debajo de esto un gesto es un clic, no un arrastre.
const SCRUB_THRESHOLD_PX = 4
// Cada pulsación de flecha con el chip enfocado.
const KEY_FACTOR = 1.1

/**
 * Zoom del lienzo: − · chip con el porcentaje · + · encuadrar.
 *
 * El chip es el control rápido: un clic vuelve a 100 % y arrastrarlo en
 * horizontal cambia el zoom como un deslizador, sin ocupar el sitio de uno
 * (ni el de un joystick: moverse por el lienzo ya se hace arrastrando).
 * En pantallas táctiles los − y + se ocultan (CSS): el pellizco ya hace zoom.
 */
const ZoomControl = ({ onFit }) => {
    const { canvas, getZoom, onSetZoom, onZoomIn, onZoomOut, getZoomLimits } = useTools()
    const [zoom, setZoom] = useState(() => getZoom())
    const drag = useRef(null)

    // El zoom lo cambian también la rueda, el pellizco y el encuadre: se lee del
    // lienzo, que es quien manda, en cada pintado.
    useEffect(() => {
        if (!canvas) return undefined
        const read = () => {
            const next = getZoom()
            setZoom((current) => (Math.abs(current - next) < 0.0005 ? current : next))
        }
        read()
        canvas.on('after:render', read)
        return () => canvas.off('after:render', read)
    }, [canvas, getZoom])

    const { min, max } = getZoomLimits()
    const atMin = zoom <= min * 1.001
    const atMax = zoom >= max * 0.999

    const handlePointerDown = (event) => {
        event.currentTarget.setPointerCapture?.(event.pointerId)
        drag.current = { startX: event.clientX, startZoom: getZoom(), moved: false }
    }

    const handlePointerMove = (event) => {
        const current = drag.current
        if (!current) return
        const deltaX = event.clientX - current.startX
        if (!current.moved && Math.abs(deltaX) < SCRUB_THRESHOLD_PX) return
        current.moved = true
        onSetZoom(current.startZoom * Math.exp(deltaX * SCRUB_RATE))
    }

    const handlePointerEnd = (event) => {
        const current = drag.current
        drag.current = null
        event.currentTarget.releasePointerCapture?.(event.pointerId)
        if (current && !current.moved && event.type === 'pointerup') onSetZoom(1)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault()
            onSetZoom(getZoom() * KEY_FACTOR)
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault()
            onSetZoom(getZoom() / KEY_FACTOR)
        } else if (event.key === 'Enter' || event.key === ' ') {
            // El ratón se resuelve en pointerup; esto es solo el teclado.
            event.preventDefault()
            onSetZoom(1)
        }
    }

    const percent = Math.round(zoom * 100)

    return (
        <div className="customiz3r-viewport-dock__group customiz3r-zoom" role="group" aria-label="Zoom">
            <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost tooltip tooltip-top customiz3r-zoom__step"
                data-tip="Alejar"
                aria-label="Alejar"
                disabled={atMin}
                onClick={onZoomOut}
            >
                <Minus className="h-[1.1rem] w-[1.1rem]" />
            </button>
            <button
                type="button"
                className="customiz3r-zoom__chip tooltip tooltip-top"
                data-tip="Clic: 100 % · arrastra: ajustar"
                aria-label={`Zoom ${percent} %. Intro: 100 %. Flechas: ajustar`}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerEnd}
                onPointerCancel={handlePointerEnd}
                onKeyDown={handleKeyDown}
            >
                {percent} %
            </button>
            <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost tooltip tooltip-top customiz3r-zoom__step"
                data-tip="Acercar"
                aria-label="Acercar"
                disabled={atMax}
                onClick={onZoomIn}
            >
                <Plus className="h-[1.1rem] w-[1.1rem]" />
            </button>
            <button
                type="button"
                className="btn btn-sm btn-circle btn-ghost tooltip tooltip-top tooltip-end"
                data-tip="Encuadrar el dibujo"
                aria-label="Encuadrar el dibujo"
                onClick={onFit}
            >
                <Maximize className="h-[1.1rem] w-[1.1rem]" />
            </button>
        </div>
    )
}

export default ZoomControl
